import { GenericMetaNode } from "./GenericMetaNode";
import { json, KeyValue, MaterialMap, MetadataRow } from "../../global";
import { PModel3d } from "../properties/PModel3d";
import { PNodeRef } from "../properties/PNodeRef";
import { MaterialJson } from "./MaterialJson";
import { PVector2 } from "../properties/PVector2";

export class ModelMaterialRelation extends GenericMetaNode {
  Objekt: string;
  updateSceneCalled = false;
  MaterialMap: MaterialMap;
  ShuffleMap: KeyValue;

  constructor(metaTable, meta) {
    super(metaTable, meta);
    this.changed("json", () => this.toJSON());
  }

  /**
   * wird beim Anzeigen im Editor aufgerufen
   */
  onShow() {
    this._resetProperties();
  }

  fromMetadataRow(row) {
    super.fromMetadataRow(row);
    const materialNames = this.getMaterialNames();
    materialNames.forEach((name) => (this["CHANGE_" + name] = row.MaterialMap?.[name]));
    materialNames.forEach(
      (name) => (this["SHUFFLE_" + name] = row.Json?.ShuffleMap?.[name] || [0, 0]),
    );
    return this;
  }

  toMetadataRow(): MetadataRow {
    this.updateSpecialData();
    return {
      id: this["id"],
      Objekt: this["Objekt"],
      Model: this.Model,
      MaterialMap: this["MaterialMap"],
      Json: { ShuffleMap: this.ShuffleMap },
      Mirrored: this["Mirrored"],
      ModelLight: this["ModelLight"],
    } as MetadataRow;
  }

  get Path() {
    return this.meta["Model"]?.Path;
  }

  set Path(v) {}

  get Model() {
    return this.meta["Model"]?.Key;
  }

  set Model(v) {
    this.meta["Model"] = APP.richMetadata["findModel"](v);
  }

  updateSpecialData() {
    const materialNames = this.getMaterialNames();
    this.MaterialMap = {};
    materialNames.forEach(
      (name) => this["CHANGE_" + name] && (this.MaterialMap[name] = this["CHANGE_" + name]),
    );
    this.ShuffleMap = {};
    materialNames.forEach(
      (name) => this["SHUFFLE_" + name] && (this.ShuffleMap[name] = this["SHUFFLE_" + name]),
    );
  }

  init() {
    super.init();
    const modelNode = APP.sceneNode.getMetaNodeLazy(
      this.meta["Model"],
      APP.richMetadata.getMetaTable("Model"),
    );
    if (modelNode) {
      this.updateSpecialData();
    }
    this.changed("json");
    return this;
  }

  updateScene() {
    this.updateSceneCalled = true;
    this._dataChanged = {};
    if (!this.changed("json", () => this.toJSON())) {
      return;
    }
    if (!this.meta["Model"]) {
      return;
    }
    const modelNode = APP.sceneNode.getMetaNodeLazy(
      this.meta["Model"],
      APP.richMetadata.getMetaTable("Model"),
    );
    if (!modelNode) {
      return;
    }
    this.updateSpecialData();
    this.updateMeta();
    console.log(this);

    APP.sceneNode.resetScene();

    // const materialNames = this.getMaterialNames();
    // materialNames.forEach(materialName=>
    //     materialShuffleMap.delete(materialName));
    // const affectedObjects = [modelNode.object3d].concat(Scenes.filter(APP.scene,o=>o.name==this.Objekt)).filter(o=>o);
    // affectedObjects.forEach(threeValue=>{
    //     Scenes.filter(threeValue,o=>o.material)
    //         .forEach(o=>{
    //             const originalName = o.material.userData.originalName ?? o.material.name;
    //             if (originalName) {
    //                 const replacingName = this.MaterialMap[originalName];
    //                 if (replacingName) {
    //                     const materialChange = APP.materials.create(replacingName);
    //                     o.material = materialChange.clone();
    //                     o.material.userData.originalName = originalName;
    //                     o.material.needsUpdate = true;
    //                 }
    //             }
    //         });
    // });
    // affectedObjects.filter(o=>o?.parent?.maybeShuffleMaterialTextures).forEach(o=>o.parent.maybeShuffleMaterialTextures());
  }

  getMaterialNames() {
    const materialNames = new Set(
      this.meta["Model"]?.ModelData?.materialNames ?? [],
    ).toArray() as string[];
    return materialNames;
  }

  createProperties(): any {
    const materialNames = this.getMaterialNames();
    const properties = super
      .createProperties()
      .concat([
        new PModel3d({
          name: "Path",
          label: null,
          editable: false,
          node: this,
        }),
      ])
      .concat(
        materialNames.map(
          (name) =>
            new PNodeRef({
              name: "CHANGE_" + name,
              label: name,
              NodeClass: MaterialJson,
              nodeList: (p) => APP.sceneNode.findProperty("MaterialJson"),
              metadataPersistent: false,
              node: this,
            }),
        ),
      )
      .concat(
        materialNames.flatMap((name) => [
          new PVector2({
            name: "SHUFFLE_" + name,
            label: name,
            type: "Vector2",
            dimensionLabels: ["u", "v"],
            comment:
              "uv-Texturangaben für Profile: wenn jeweils größer als 1, dann werden in dieser " +
              "Koordinate entsprechend viele zufällig verschobene Kopien der Texturen verwendet. " +
              "Die Textur wird dabei als nicht-tileable angesehen. " +
              "Voraussetzung ist, dass die Textur entsprechend viel größer ist als das Objekt. " +
              "Für die i-te Kopie aus n Kopien ist die Verschiebung dann (i % (n-1))/n. " +
              "Wenn sowohl u als auch v ungleich 0 sind, dann werden u*v Kopien erzeugt. " +
              "Negative Werte sind möglich - dann wird die Textur zusätzlich gespiegelt. " +
              "Beispiel: 9 erzeugt 9 Kopien, -9 erzeugt 18 Kopien, wobei die Hälfte gespiegelt ist.",
            default: [0, 0],
            node: this,
          }),
        ]),
      );
    // @ts-ignore
    return this.orderAndGroup(
      [
        { group: "Preview", open: true },
        { name: "Path" },
        { group: "Hauptparameter", open: true },
        { unused: true },
        { name: "Objekt" },
        { group: "Materialaustausch", open: true },
      ]
        .concat(materialNames.map((name) => ({ name: "CHANGE_" + name })))
        .concat([{ group: "Textur zufällig verschieben", open: true }])
        .concat(materialNames.map((name) => ({ name: "SHUFFLE_" + name })))
        .concat([
          // @ts-ignore
          { name: "MaterialMap", showInEditor: () => false },
          // {name: 'Model', showInEditor: ()=>false},
        ]),
      properties,
    );
  }
}
